import * as XLSX from "xlsx";

function makeUniqueHeaders(cells) {
  const count = new Map();
  return cells.map((cell, index) => {
    const base = String(cell ?? "").trim() || `Columna_${index + 1}`;
    const n = (count.get(base) || 0) + 1;
    count.set(base, n);
    return n === 1 ? base : `${base}_${n}`;
  });
}

// Normaliza el valor de una celda para que el resto de la app lo consuma
// siempre con el mismo formato, sin depender del formato visual del Excel.
// - Date  -> string ISO "YYYY-MM-DD" (estable, sin ambigüedad de locale)
// - null/undefined -> ""
// - resto -> tal cual (number, string, boolean)
function normalizeCellValue(val) {
  if (val === undefined || val === null) return "";
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, "0");
    const d = String(val.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return val;
}

/**
 * Lee la primera hoja de un Excel; la primera fila son encabezados.
 * @param {File} file
 * @param {{ maxPreviewRows?: number }} options
 * @returns {Promise<{ headers: string[], rows: object[], rowCount: number, fileName: string }>}
 */
export async function parseExcelPreview(file, options = {}) {
  const { maxPreviewRows = 50 } = options;

  try {
    const buffer = await file.arrayBuffer();
    // cellDates: true => las celdas con formato fecha llegan como Date reales,
    // sin importar el formato visual del Excel (dd/mm/yyyy, m/d/yyyy, etc.).
    const workbook = XLSX.read(buffer, { type: "array", cellDates: true });

    if (!workbook.SheetNames?.length) {
      return { headers: [], rows: [], rowCount: 0, fileName: file.name };
    }

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    // raw: true para preservar tipos (Date, number) y normalizarlos abajo
    // de forma consistente, en lugar de depender del formato visual del Excel.
    const matrix = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: true,
    });

    if (!matrix.length) {
      return { headers: [], rows: [], rowCount: 0, fileName: file.name };
    }

    const headerRow = matrix[0];
    const rawLabels = headerRow.map((cell, index) => {
      const label = String(cell ?? "").trim();
      return label || `Columna_${index + 1}`;
    });
    const headers = makeUniqueHeaders(rawLabels);

    const dataRows = matrix
      .slice(1)
      .filter((row) => Array.isArray(row) && row.some((c) => c !== "" && c != null));

    const rowCount = dataRows.length;
    const previewSlice = dataRows.slice(0, maxPreviewRows);

    const rows = previewSlice.map((row, rowIndex) => {
      const obj = { id: rowIndex };
      headers.forEach((header, colIndex) => {
        obj[header] = normalizeCellValue(row[colIndex]);
      });
      return obj;
    });

    return {
      headers,
      rows,
      rowCount,
      fileName: file.name,
    };
  } catch (err) {
    const msg = err?.message ? String(err.message) : "Error desconocido";
    throw new Error(`No se pudo leer el archivo Excel: ${msg}`);
  }
}

/**
 * Genera y descarga un .xlsx con una sola fila de encabezados (plantilla vacía).
 * @param {string} fileName - Nombre del archivo (con o sin .xlsx)
 * @param {string[]} headers
 * @param {{ sheetName?: string }} [options]
 */
export function downloadTemplateXlsx(fileName, headers, options = {}) {
  const { sheetName = "Plantilla" } = options;
  const name = fileName.toLowerCase().endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, name);
}
